import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, CalendarClock, ClipboardCheck, ListTodo, Factory,
  FileBarChart2, Settings, Bell, History, User, ChevronsLeft, ChevronsRight, Calculator, ShieldCheck
} from "lucide-react";
import { useAuth, hasRole } from "@/context/AuthContext";
import { TID } from "@/constants/testIds";

// Sidebar navigation. Items can be plain nav links or a "section" (a non-clickable
// header) whose subsequent entries with `parent === section.key` are indented under it.
const NAV = [
  { key: "dashboard", to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["system_admin","sd_admin","auditor","manager","supplier"] },
  { key: "planning", to: "/planning", label: "Audit Planning", icon: CalendarClock, roles: ["system_admin","sd_admin","manager"] },
  { key: "execution", to: "/audits", label: "Audit Execution", icon: ClipboardCheck, roles: ["system_admin","sd_admin","auditor","manager"] },
  { key: "assessment", to: "/review", label: "Assessment", icon: ListTodo, roles: ["system_admin","manager"] },
  { key: "suppliers", to: "/suppliers", label: "Supplier Management", icon: Factory, roles: ["system_admin","sd_admin","manager"] },
  { key: "reports", to: "/reports", label: "Reports", icon: FileBarChart2, roles: ["system_admin","sd_admin","auditor","manager","supplier"] },

  // ─── System Parameter (parent section) ─────────────────────────────
  { type: "section", key: "system-parameter", label: "System Parameter", icon: Settings, roles: ["system_admin","sd_admin"] },
  { key: "administration", parent: "system-parameter", to: "/admin", label: "Administration", icon: ShieldCheck, roles: ["system_admin"] },
  { key: "formula", parent: "system-parameter", to: "/admin/formula-engine", label: "Formula Engine", icon: Calculator, roles: ["system_admin","sd_admin"] },

  // CAPA menu temporarily disabled per stakeholder request (2026-01)
  // { key: "capa", to: "/capa", label: "CAPA Tracker", icon: ListTodo, roles: ["system_admin","sd_admin","auditor","manager"] },
  { key: "notifications", to: "/notifications", label: "Notification Center", icon: Bell, roles: ["system_admin","sd_admin","auditor","manager","supplier"] },
  { key: "trail", to: "/audit-trail", label: "Audit Trail", icon: History, roles: ["system_admin","manager"] },
  { key: "account", to: "/account", label: "My Account", icon: User, roles: ["system_admin","sd_admin","auditor","manager","supplier"] },
];

export default function Sidebar({ collapsed, onToggle, appName }) {
  const { user } = useAuth();
  const location = useLocation();

  // Filter by role. For section headers, hide if no accessible children exist.
  const visible = NAV.filter((n) => hasRole(user, ...(n.roles || [])));
  const items = visible.filter((n) => {
    if (n.type !== "section") return true;
    return visible.some((c) => c.parent === n.key);
  });

  return (
    <aside
      data-testid={TID.shell.sidebar}
      className={`sidebar-dark flex flex-col shrink-0 transition-[width] duration-300 ease-out ${collapsed ? "w-[64px]" : "w-[240px]"}`}
    >
      <div className="h-16 flex items-center px-4 border-b border-white/5">
        {!collapsed ? (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-sams-primary grid place-items-center text-white font-black">S</div>
            <div>
              <div className="font-heading font-extrabold tracking-tight text-white text-lg leading-none">{appName || "SAMS"}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mt-1">Supplier Audit</div>
            </div>
          </div>
        ) : (
          <div className="w-8 h-8 rounded-md bg-sams-primary grid place-items-center text-white font-black mx-auto">S</div>
        )}
      </div>

      <nav className="flex-1 py-3 overflow-y-auto">
        {items.map((item) => {
          const Icon = item.icon;

          // Non-clickable section header (parent)
          if (item.type === "section") {
            return (
              <div
                key={item.key}
                data-testid={TID.shell.sidebarItem(item.key)}
                className="mt-3 mb-1 px-4 flex items-center gap-3 py-2 text-slate-400 text-sm select-none"
              >
                <Icon size={18} strokeWidth={1.75} className="sidebar-icon shrink-0" />
                {!collapsed && <span className="truncate font-semibold uppercase tracking-[0.14em] text-[10px]">{item.label}</span>}
              </div>
            );
          }

          const active = location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));
          const indent = !!item.parent;
          return (
            <NavLink
              key={item.key}
              to={item.to}
              data-testid={TID.shell.sidebarItem(item.key)}
              className={`sidebar-item flex items-center gap-3 py-2.5 text-sm ${indent ? "pl-9 pr-4" : "px-4"} ${active ? "sidebar-item-active text-white" : "text-slate-300"}`}
            >
              <Icon size={indent ? 15 : 18} strokeWidth={1.75} className="sidebar-icon shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      <button
        onClick={onToggle}
        data-testid={TID.shell.sidebarToggle}
        className="mx-3 mb-4 flex items-center justify-center gap-2 py-2 text-slate-300 hover:text-white text-xs rounded-md bg-white/5 hover:bg-white/10 transition-colors"
        aria-label="Toggle sidebar"
      >
        {collapsed ? <ChevronsRight size={16} /> : (<><ChevronsLeft size={16} /><span>Collapse</span></>)}
      </button>
    </aside>
  );
}
